import { useMemo } from 'react';

import { useUserStateContext } from '@/shared/components/UserStateProvider';
import useListSeasonsQuery from './useListSeasonsQuery';

/**
 * Resolves the season containing `date`, for views that select a day rather
 * than a season.
 *
 * The games API requires a seasonId. Day-scoped operator views let the user
 * jump to an arbitrary date, so pinning the league's current season would
 * return nothing the moment they navigate into a past one — the season has to
 * follow the date.
 */
export const useSeasonIdForDate = (date: Date) => {
  const { currentLeague } = useUserStateContext();
  const { data, isPlaceholderData } = useListSeasonsQuery(currentLeague?.id ?? 0);

  const seasonId = useMemo(() => {
    const day = date.getTime();
    // startDate/endDate are mapped to Date objects by the query but typed string.
    const time = (value: unknown): number => new Date(value as string).getTime();
    const containing = (data?.seasons ?? []).filter(
      (season) => time(season.startDate) <= day && day <= time(season.endDate),
    );

    // Overlapping windows (Pre bleeding into Regular): the later start wins,
    // matching pickCurrentSeasonId in useListSeasonsQuery.
    if (containing.length) {
      return containing.reduce((a, b) => (time(b.startDate) > time(a.startDate) ? b : a)).id;
    }

    // A day in an offseason gap holds no games either way, so fall back to the
    // current season to keep the games query enabled rather than leaving it
    // silently disabled with nothing on screen to explain why.
    return data?.currentSeasonId ?? undefined;
  }, [data, date]);

  // The query serves placeholderData, so its status is 'success' with an empty
  // list before anything loads and isPending never fires — isPlaceholderData is
  // the real "no seasons yet" test. With no league there is nothing to wait for.
  return { seasonId, isLoading: isPlaceholderData && !!currentLeague?.id };
};

export default useSeasonIdForDate;
